import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const PosOperations = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [tables, setTables] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    available: '#10b981',
    occupied: '#ef4444',
    reserved: '#f59e0b'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
    // Auto-refresh every 10 seconds to get latest order statuses from kitchen
    const interval = setInterval(fetchAllData, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchTables(), fetchActiveOrders()]);
    setLoading(false);
  };

  const fetchTables = async () => {
    try {
      const data = await apiGet('/admin/pos/operations/tables');
      if (data.success) setTables(data.data);
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const fetchActiveOrders = async () => {
    try {
      const data = await apiGet('/admin/pos/operations/active-orders');
      if (data.success) setActiveOrders(data.data);
    } catch (error) {
      console.error('Error fetching active orders:', error);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const data = await apiGet('/admin/pos/operations/orders/${orderId}');
      if (data.success) {
        setOrderDetails(data.data);
        setShowOrderDetails(true);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/pos/operations/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Order status updated!');
        fetchAllData();
        if (showOrderDetails) {
          fetchOrderDetails(orderId);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      available: colors.available,
      occupied: colors.occupied,
      reserved: colors.reserved,
      cleaning: colors.textLight,
      pending: colors.warning,
      preparing: colors.accent,
      ready: colors.success,
      served: colors.primary
    };
    return statusColors[status] || colors.textLight;
  };

  const tablesByFloor = tables.reduce((acc, table) => {
    if (!acc[table.floor]) acc[table.floor] = [];
    acc[table.floor].push(table);
    return acc;
  }, {});

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    tableGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(4, 1fr)' : 'repeat(5, 1fr)',
      gap: isMobile ? '10px' : '12px'
    },
    tableCard: {
      padding: isMobile ? '14px' : '16px',
      borderRadius: '10px',
      border: '2px solid',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading operations...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>POS Operations</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Tables & kitchen' : 'Manage restaurant tables, kitchen operations, and dining services'}
        </p>
      </div>

      {/* Active Orders */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}><span>📋</span> Active Orders</h2>
        {activeOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No active orders
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {activeOrders.map(order => (
              <div
                key={order.id}
                style={{
                  padding: isMobile ? '12px' : '14px',
                  background: colors.lightGray,
                  borderRadius: '8px',
                  borderLeft: `4px solid ${getStatusColor(order.order_status)}`,
                  cursor: 'pointer'
                }}
                onClick={() => fetchOrderDetails(order.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                  <div>
                    <strong style={{ fontSize: isMobile ? '14px' : '16px' }}>{order.order_number}</strong>
                    {order.table_number && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: colors.textLight }}>
                        Table {order.table_number}
                      </span>
                    )}
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: `${getStatusColor(order.order_status)}20`,
                    color: getStatusColor(order.order_status)
                  }}>
                    {order.order_status}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px' }}>
                  {order.items_summary}
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: colors.primary }}>
                  ₹{parseFloat(order.final_amount).toLocaleString('en-IN')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tables by Floor */}
      {Object.keys(tablesByFloor).sort((a, b) => a - b).map(floor => (
        <div key={floor} style={styles.contentCard}>
          <h2 style={styles.sectionTitle}>
            <span>🏢</span> Floor {floor}
          </h2>
          <div style={styles.tableGrid}>
            {tablesByFloor[floor].map(table => (
              <div
                key={table.id}
                style={{
                  ...styles.tableCard,
                  borderColor: getStatusColor(table.status),
                  background: `${getStatusColor(table.status)}15`
                }}
                onClick={() => table.current_order_id && fetchOrderDetails(table.current_order_id)}
                onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1)')}
              >
                <div style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '700', marginBottom: '4px' }}>
                  {table.table_number}
                </div>
                <div style={{ fontSize: isMobile ? '10px' : '11px', color: colors.textLight, marginBottom: '6px' }}>
                  Capacity: {table.capacity}
                </div>
                <div style={{
                  fontSize: isMobile ? '9px' : '10px',
                  fontWeight: '600',
                  color: getStatusColor(table.status),
                  textTransform: 'uppercase'
                }}>
                  {table.status}
                </div>
                {table.order_number && (
                  <div style={{ fontSize: '10px', color: colors.textDark, marginTop: '4px' }}>
                    {table.order_number}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Order Details Modal */}
      {showOrderDetails && orderDetails && (
        <div style={styles.modal} onClick={() => setShowOrderDetails(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              Order Details
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
                {orderDetails.order.order_number}
              </div>
              <div style={{ fontSize: '13px', color: colors.textLight }}>
                {orderDetails.order.table_number && `Table ${orderDetails.order.table_number} • `}
                {orderDetails.order.order_type.replace('_', ' ')}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px' }}>Items:</h4>
              {orderDetails.items.map(item => (
                <div key={item.id} style={{
                  padding: '10px',
                  background: colors.lightGray,
                  borderRadius: '6px',
                  marginBottom: '6px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600' }}>{item.item_name}</div>
                      <div style={{ fontSize: '11px', color: colors.textLight }}>
                        Qty: {item.quantity} × ₹{parseFloat(item.price).toFixed(2)}
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: colors.primary }}>
                      ₹{parseFloat(item.subtotal).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ borderTop: `2px solid ${colors.mediumGray}`, paddingTop: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span>Subtotal:</span>
                <span style={{ fontWeight: '600' }}>₹{parseFloat(orderDetails.order.total_amount).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span>Tax:</span>
                <span>₹{parseFloat(orderDetails.order.tax_amount).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: colors.primary }}>
                <span>Total:</span>
                <span>₹{parseFloat(orderDetails.order.final_amount).toFixed(2)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <button
                onClick={() => updateOrderStatus(orderDetails.order.id, 'preparing')}
                style={{ ...styles.button, background: colors.accent, color: colors.white }}
              >
                Mark Preparing
              </button>
              <button
                onClick={() => updateOrderStatus(orderDetails.order.id, 'ready')}
                style={{ ...styles.button, background: colors.success, color: colors.white }}
              >
                Mark Ready
              </button>
              <button
                onClick={() => updateOrderStatus(orderDetails.order.id, 'served')}
                style={{ ...styles.button, background: colors.primary, color: colors.white }}
              >
                Mark Served
              </button>
            </div>

            <button
              onClick={() => setShowOrderDetails(false)}
              style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, width: '100%' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default PosOperations;